import { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Square, MapPin, Tag, FileText, Phone, Mail, Building2, Store, ShoppingBag, Utensils, Laptop, ShoppingCart, Scissors, BookOpen, Dumbbell, Gem, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ApplicationModal from '../components/application/ApplicationModal';
import { apiService } from '../services/api';
import { siteImages } from '../data/siteImages';

const iconMap: Record<string, JSX.Element> = {
  Pharmacy: <Building2 className="h-20 w-20 text-primary" />,
  'Clothing Store': <ShoppingBag className="h-20 w-20 text-primary" />,
  Confectionary: <Utensils className="h-20 w-20 text-primary" />,
  Electronics: <Laptop className="h-20 w-20 text-primary" />,
  Groceries: <ShoppingCart className="h-20 w-20 text-primary" />,
  'Beauty Salon': <Scissors className="h-20 w-20 text-primary" />,
  Bookstore: <BookOpen className="h-20 w-20 text-primary" />,
  'Sports Goods': <Dumbbell className="h-20 w-20 text-primary" />,
  Jewelry: <Gem className="h-20 w-20 text-primary" />,
  'Home & Garden': <Home className="h-20 w-20 text-primary" />,
  Office: <Building2 className="h-20 w-20 text-primary" />,
  'Gaming Hub': <Laptop className="h-20 w-20 text-primary" />,
  'Mens Wear': <ShoppingBag className="h-20 w-20 text-primary" />,
  'Womens Wear': <ShoppingBag className="h-20 w-20 text-primary" />,
  'Childrens Wear': <ShoppingBag className="h-20 w-20 text-primary" />,
  'Baby Wear': <ShoppingBag className="h-20 w-20 text-primary" />,
  Shoes: <ShoppingBag className="h-20 w-20 text-primary" />,
  Bags: <ShoppingBag className="h-20 w-20 text-primary" />,
  Accessories: <Gem className="h-20 w-20 text-primary" />,
  Watches: <Gem className="h-20 w-20 text-primary" />,
  Other: <Store className="h-20 w-20 text-primary" />
};

const statusClass: Record<string, string> = {
  available: 'bg-primary-light text-primary',
  reserved: 'bg-amber-100 text-amber-800',
  rented: 'bg-stone-100 text-stone-700',
  unavailable: 'bg-red-100 text-red-800'
};

function getIcon(purpose: string) {
  return iconMap[purpose] ?? <Store className="h-20 w-20 text-primary" />;
}

export default function ShopDetail() {
  const { id } = useParams();
  const [shop, setShop] = useState<any>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadShop();
  }, [id]);

  const loadShop = async () => {
    if (!id || id === 'undefined' || id === 'null') {
      setError('Invalid shop ID');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getShop(id);
      if (response.status === 'success' && response.data?.shop) {
        setShop(response.data.shop);
      } else {
        setError('Shop not found');
      }
    } catch (err) {
      console.error('Error loading shop:', err);
      setError('Failed to load shop details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4" />
          <p className="text-ink-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="max-w-md w-full p-8 rounded-2xl bg-surface-raised shadow-card text-center">
          <p className="text-ink mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={loadShop}
              className="h-11 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
            >
              Try Again
            </button>
            <Link
              to="/shops"
              className="h-11 px-6 rounded-xl border border-stone-300 text-ink font-medium inline-flex items-center justify-center"
            >
              Back to Shops
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!shop) return <Navigate to="/shops" replace />;

  const statusLabel = String(shop.status).charAt(0).toUpperCase() + String(shop.status).slice(1);
  const statusStyle = statusClass[shop.status] ?? statusClass.unavailable;

  return (
    <div className="min-h-screen bg-surface py-16 lg:py-20">
      <Helmet>
        <title>Shop {shop.shopNumber} – The Gandi Mall</title>
        <meta name="description" content={`${shop.purpose} space, ${shop.size} sqm, ${shop.floor} floor at Gandi Mall.`} />
      </Helmet>
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/shops"
          className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary-dark mb-8"
        >
          <ArrowLeft size={18} /> Back to listings
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl overflow-hidden shadow-card">
              <div className="relative min-h-[220px] lg:min-h-[260px]">
                <img
                  src={siteImages.retailInterior}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-ink/40 flex items-center justify-center p-6">
                  <div className="rounded-2xl bg-white/95 p-6 shadow-card">
                    {getIcon(shop.purpose)}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-surface-raised shadow-card p-6 lg:p-8">
              <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-ink">Shop {shop.shopNumber}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyle}`}>
                  {statusLabel}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex gap-3 p-4 rounded-xl bg-surface-muted">
                  <Square className="h-6 w-6 text-primary shrink-0" />
                  <div>
                    <div className="font-semibold text-ink">{Number(shop.size).toFixed(1)} sqm</div>
                    <div className="text-sm text-ink-muted">Floor area</div>
                  </div>
                </div>
                <div className="flex gap-3 p-4 rounded-xl bg-surface-muted">
                  <MapPin className="h-6 w-6 text-primary shrink-0" />
                  <div>
                    <div className="font-semibold text-ink">{shop.floor} Floor</div>
                    <div className="text-sm text-ink-muted">Location</div>
                  </div>
                </div>
                <div className="flex gap-3 p-4 rounded-xl bg-surface-muted sm:col-span-2">
                  <Tag className="h-6 w-6 text-primary shrink-0" />
                  <div>
                    <div className="font-semibold text-ink">{shop.purpose}</div>
                    <div className="text-sm text-ink-muted">Use</div>
                  </div>
                </div>
              </div>
              {shop.floorplanUrl && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-ink mb-4">Floor plan</h3>
                  <div className="rounded-xl bg-surface-muted p-8 flex items-center justify-center gap-3 text-ink-muted">
                    <FileText size={32} /> Available on request
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl bg-surface-raised shadow-card p-6">
                <h3 className="text-lg font-bold text-ink mb-2">Interested?</h3>
                <p className="text-ink-muted text-sm mb-6">Submit your leasing application.</p>
                {shop.status === 'available' ? (
                  <button
                    type="button"
                    onClick={() => setShowApplicationModal(true)}
                    className="w-full h-12 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
                  >
                    Apply for this shop
                  </button>
                ) : (
                  <p className="text-sm text-ink-muted">This shop is {shop.status}. Join waitlist.</p>
                )}
              </div>
              <div className="rounded-2xl bg-surface-raised shadow-card p-6">
                <h3 className="text-lg font-bold text-ink mb-4">Contact</h3>
                <a href="tel:+263717082807" className="flex gap-3 text-ink hover:text-primary mb-3">
                  <Phone size={20} /> +263 71 708 2807
                </a>
                <a href="mailto:info@persimmonprop.com" className="flex gap-3 text-ink hover:text-primary break-all">
                  <Mail size={20} /> info@persimmonprop.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showApplicationModal && (
        <ApplicationModal shop={shop} onClose={() => setShowApplicationModal(false)} />
      )}
    </div>
  );
}
